import React, { Component } from 'react';
import { Button, Form, Alert, Card, Container, Row, Col } from 'react-bootstrap';
import { GoPlus } from 'react-icons/go';
import WorkspaceForm from './workspaceForm';
import Dialog from '../../components/modal';
import WidgetStatus from '../../../common/widgetStatus';
import './workspaces.css';

/**
 * WorkspacesSurface — workspace template library editor.
 *
 * Layout: [ Library rail (188px) ][ Form (flex 1) ]
 *
 * Lifecycle:
 *   - On mount, loads workspace templates via client.getTemplates().
 *   - Selecting a workspace shows its structured form (repos + skill packs).
 *   - Editing immediately updates local state.
 *   - Save persists via client.saveWorkspaces().
 *   - "New" creates a blank workspace template.
 *
 * Storage shape: workspaces is a LIST [{ name, type, repos, packs }, ...]
 * Each template carries its own name. Runtime indexes built at use sites.
 *
 * Props:
 *   store      — model store (not directly used; passed for consistency)
 *   client     — SystemOfWorkClient instance
 *   selection  — shell-level selection (not used by this surface)
 *   onSelect   — (item) => void  (not used by this surface)
 */
class WorkspacesSurface extends Component {
  constructor(props) {
    super(props);
    this.state = {
      /** Full workspaces list: [{ name, type, repos, packs }, ...] */
      workspaces: [],
      /** Currently selected workspace template name */
      selectedWorkspace: null,
      loading: true,
      loadError: null,
      saving: false,
      saveStatus: null,
      addWsModal: false,
      addWsName: '',
      addWsNameError: null,
    };
    this._mounted = false;
  }

  componentDidMount() {
    this._mounted = true;
    this._load();
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  async _load() {
    const { client } = this.props;
    if (!client) {
      this.setState({ loading: false });
      return;
    }
    this.setState({ loading: true, loadError: null });
    try {
      const { workspaces } = await client.getTemplates();
      if (!this._mounted) return;
      const wsList = workspaces || [];
      this.setState({
        workspaces: wsList,
        loading: false,
        selectedWorkspace: wsList.length > 0 ? wsList[0].name : null,
      });
    } catch (e) {
      if (!this._mounted) return;
      this.setState({ loading: false, loadError: 'Failed to load workspace templates.' });
    }
  }

  /** Find a workspace template object by name from the list. */
  _findWorkspace(name) {
    return this.state.workspaces.find(ws => ws.name === name) || null;
  }

  _selectWorkspace(name) {
    this.setState({ selectedWorkspace: name, saveStatus: null });
  }

  _handleWorkspaceChange(updatedWs) {
    const { workspaces, selectedWorkspace } = this.state;
    if (!selectedWorkspace) return;
    const updatedWorkspaces = workspaces.map(ws =>
      ws.name === selectedWorkspace ? { ...updatedWs, name: selectedWorkspace } : ws
    );
    this.setState({ workspaces: updatedWorkspaces, saveStatus: null });
  }

  _addNewWorkspace() {
    this.setState({ addWsModal: true, addWsName: '', addWsNameError: null });
  }

  _submitNewWorkspace() {
    const { addWsName, workspaces } = this.state;
    if (!addWsName.trim()) return;
    const name = addWsName.trim().replace(/\s+/g, '_');
    if (workspaces.some(ws => ws.name === name)) {
      this.setState({ addWsNameError: `Workspace "${name}" already exists.` });
      return;
    }
    const blank = { name, type: 'local_folder', repos: [], packs: [] };
    this.setState({
      workspaces: [...workspaces, blank],
      selectedWorkspace: name,
      saveStatus: null,
      addWsModal: false,
    });
  }

  async _save() {
    const { client } = this.props;
    const { workspaces } = this.state;
    if (!client) return;
    this.setState({ saving: true, saveStatus: null });
    try {
      await client.saveWorkspaces(workspaces);
      if (!this._mounted) return;
      this.setState({ saving: false, saveStatus: 'saved' });
    } catch (e) {
      if (!this._mounted) return;
      this.setState({ saving: false, saveStatus: 'error' });
    }
  }

  render() {
    const {
      workspaces, selectedWorkspace,
      loading, loadError, saving, saveStatus,
      addWsModal, addWsName, addWsNameError,
    } = this.state;

    if (loading) {
      return (
        <div className="mc-ws-container">
          <WidgetStatus variant="primary" loading>Loading…</WidgetStatus>
        </div>
      );
    }

    if (loadError) {
      return (
        <div className="mc-ws-container">
          <WidgetStatus variant="warning">{loadError}</WidgetStatus>
        </div>
      );
    }

    const ws = selectedWorkspace ? this._findWorkspace(selectedWorkspace) : null;

    return (
      <Container fluid className="mc-ws-container">
        <Row className="mc-grid-row">
          {/* Library rail — panel Card identical to this surface's form panel
              (mc-ws-main-card), so the left and right columns read as one surface. */}
          <Col xs="auto" className="d-flex flex-column mc-ws-rail-col">
            <Card className="mc-ws-main-card">
              <Card.Body className="mc-card-flush">
                <div className="mc-rail">
                  <Card.Text as="h6">Workspaces</Card.Text>
                  <hr />
                  <Button variant="light" size="sm" className="mc-rail-new" onClick={() => this._addNewWorkspace()}>
                    <GoPlus /> New
                  </Button>

                  {workspaces.length === 0 ? (
                    <p className="text-muted" style={{ fontSize: '0.8rem', fontStyle: 'italic', padding: '6px' }}>
                      No workspace templates yet. Click &ldquo;+ New&rdquo; to create one.
                    </p>
                  ) : (
                    workspaces.map(item => (
                      <div
                        key={item.name}
                        className={'mc-rail-item' + (item.name === selectedWorkspace ? ' on' : '')}
                        onClick={() => this._selectWorkspace(item.name)}
                      >
                        <div className="mc-rail-item-name"><code>{item.name}</code></div>
                        {item.desc && (
                          <div className="mc-rail-item-desc">{item.desc}</div>
                        )}
                        {!item.desc && item.type && (
                          <div className="mc-rail-item-desc">
                            {item.type}
                            {item.repos && item.repos.length > 0
                              ? ` · ${item.repos.length} repo${item.repos.length > 1 ? 's' : ''}`
                              : ''}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Form area — flat card */}
          <Col className="d-flex flex-column">
            <Card className="mc-ws-main-card">
              <Card.Body className="mc-card-flush">
                <div className="mc-ws-main">
                  {!selectedWorkspace ? (
                    <div className="mc-ws-main-empty">
                      Select or create a workspace template.
                    </div>
                  ) : (
                    <>
                      <div className="mc-ws-main-toolbar">
                        <div style={{ flex: 1 }} />
                        {saveStatus === 'saved' && (
                          <span className="text-success" style={{ fontSize: '0.8rem', fontWeight: 600 }}>Saved</span>
                        )}
                        {saveStatus === 'error' && (
                          <span className="text-danger" style={{ fontSize: '0.8rem', fontWeight: 600 }}>Save failed</span>
                        )}
                        <Button variant="success" size="sm" disabled={saving} onClick={() => this._save()}>
                          {saving ? 'Saving…' : 'Save'}
                        </Button>
                      </div>
                      <WorkspaceForm
                        name={selectedWorkspace}
                        workspace={ws}
                        onChange={(updated) => this._handleWorkspaceChange(updated)}
                      />
                    </>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Dialog
          show={addWsModal}
          title="New Workspace"
          onHide={() => this.setState({ addWsModal: false })}
          onSubmit={() => this._submitNewWorkspace()}
          submitLabel="Create"
          submitDisabled={!addWsName.trim()}
        >
          <Form.Group style={{ marginBottom: 0 }}>
            <Form.Label><strong>Name</strong></Form.Label>
            <Form.Control
              size="sm"
              type="text"
              value={addWsName}
              onChange={e => this.setState({ addWsName: e.target.value, addWsNameError: null })}
              placeholder="e.g. my_workspace"
              autoFocus
            />
            {addWsNameError && (
              <Alert variant="warning" style={{ marginTop: 6, fontSize: '0.8rem', marginBottom: 0 }}>
                {addWsNameError}
              </Alert>
            )}
          </Form.Group>
        </Dialog>
      </Container>
    );
  }
}

export default WorkspacesSurface;
